import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import logo from "../../assets/BETM_Logo.png";
import { useDataContext } from "../DataContext/PortfolioDataContext";
import { useEntitlements } from '../DataContext/EntitlementsContext';
import { menuConfig } from "../../Data/menuConfig";
import { datadogRum } from "@datadog/browser-rum";
import { getUserInfo } from "../../services/authService-sighup"; 
import Avatar from "./Avatar";
import { FiBookOpen } from "react-icons/fi";
import { useChildApps } from "../../shellHost/ChildAppsContext";
import { childAppPath } from "../../shellHost/host";

const Header: React.FC = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { entitlements } = useEntitlements();
  const { apps: childApps } = useChildApps();
  const { setFilters } = useDataContext();

  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [, setIsOperationsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const toggleProfileDropdown = () => setIsProfileDropdownOpen(!isProfileDropdownOpen);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const user = getUserInfo();
  const fullName = user?.fullName || "";
  // const toggleOperationsMenu = () => {
  //   setIsOperationsOpen(!isOperationsOpen);
  //   setActiveMenu(isOperationsOpen ? null : "operations");
  // };

  const handleNavigate = (path: string, menu?: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsOperationsOpen(false);
    setActiveMenu(menu || path);
  };

  const handleLogout = async () => {
    const { cacheDB } = await import('../CacheDB/cacheDB');
  
    try {
      datadogRum.addAction("User logged out");
      const ddUser = localStorage.getItem("datadogUser");
      sessionStorage.clear();
      localStorage.clear();
  
      if (ddUser) {
        localStorage.setItem("datadogUser", ddUser); 
      }
      await cacheDB.responses.clear();
      setFilters({
        selectedAssetType: "",
        selectedISO: "",
        sortField: "",
        sortOrder: "Ascending",
        groupByField: "",
      });

      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      }).then(() => {
        instance.setActiveAccount(null);
        navigate("/");
      }).catch(async (popupError) => {
        console.warn("logoutPopup failed, using logoutRedirect instead", popupError);
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      });

    } catch (error) {
      console.error("Logout failed", error);
    }
  };
  

  const isActive = (path: string) =>
    activeMenu === path ||
    location.pathname === path ||
    location.pathname.startsWith(`${path}/`);

  const capitalizeFirstLetter = (str: string) => {
    if (str === "/landing") return "Portfolio Landing";
    if (str === "/administration") return "Admin";
    if (str === "/filecenter") return "File Center";
    if (str === "/custom-reports") return "Custom Reports";
    if (str === "/market-fundamentals") return "Market Fundamentals";
    if (str === "/outage-management") return "Outage Management";
    if (str === "/Revenue-Tab") return "Revenue Tab";

    return str.replace("/", "").replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

 
  const hasAccess = (path: string) =>
    entitlements.some((e) => e.po_path?.toLowerCase().trim() === path.toLowerCase().trim());

// console.log("Access",entitlements)
  const mainTabs = [
    "/landing",
    "/custom-reports",
    "/filecenter",
    "/administration",
    "/outage-management",
    "/market-fundamentals",
    "/Revenue-Tab",
  ].filter(hasAccess);

  return (
    <header className="flex justify-between items-center p-4 bg-white shadow-xl font-poppins relative z-50">
      {/* Logo */}
      <div className="flex items-center">
        <img src={logo} alt="BETM Logo" className="w-250px h-14 md:w-250px md:h-16 md:ml-4" />
      </div>

      {/* Navigation */}
      <nav className={`
        ${isMenuOpen ? "block" : "hidden"} 
        gap-6 ml-10 absolute top-full left-0 w-full bg-white shadow-md p-4 
        nav:flex nav:relative nav:top-auto nav:left-auto 
        nav:w-auto nav:bg-transparent nav:shadow-none nav:p-0
      `}>
        {/* Render tabs based on entitlements */}
        {mainTabs.map((path, index) => (
          <span
            key={index}
            onClick={() => handleNavigate(path)}
            className={`
              cursor-pointer text-base transition-all duration-300 
              ${isActive(path) ? "text-red-500" : "text-gray-600"} 
              hover:text-red-500 hover:scale-105 
              block nav:inline
            `}
          >
            {capitalizeFirstLetter(path)}
          </span>
        ))}

        {/* Child apps loaded from the entitlements app list (see shellHost) */}
        {childApps.map((childApp) => {
          const path = childAppPath(childApp.slug);
          return (
            <span
              key={childApp.slug}
              onClick={() => handleNavigate(path)}
              className={`
                cursor-pointer text-base transition-all duration-300
                ${isActive(path) ? "text-red-500" : "text-gray-600"}
                hover:text-red-500 hover:scale-105
                block nav:inline
              `}
            >
              {childApp.label}
            </span>
          );
        })}

        {/* Operations Menu */}
        {/* {menuConfig.map((menu, index) => (
          <div key={index} className="relative">
            <span
              onClick={toggleOperationsMenu}
              className={`
                cursor-pointer text-base transition-all duration-300 
                ${isActive("operations") ? "text-red-500" : "text-gray-600"} 
                hover:text-red-500 hover:scale-105 
                block nav:inline
              `}
            >
              {menu.title}
            </span>

            {isOperationsOpen && (
              <div className="absolute top-full left-0 bg-white shadow-md rounded-md w-40 p-2 z-10">
                {menu.submenu.map((subItem, subIndex) => (
                  <span
                    key={subIndex}
                    onClick={() => handleNavigate(subItem.path, "operations")}
                    className="block py-2 px-4 text-sm text-gray-600 cursor-pointer hover:text-red-500 hover:bg-gray-100 transition-all duration-300"
                  >
                    {subItem.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))} */}

      </nav>

      {/* Profile, About & Toggle Button */}
     <div className="flex items-center gap-4 ml-auto mr-2 h-full">
  <div className="flex items-center">
    <button
      type="button"
      onClick={() => handleNavigate("/knowledge-center")}
      aria-label="Open Knowledge Center"
      className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-500 transition-colors duration-300"
    >
      <FiBookOpen className="w-6 h-6" />
      <span>Knowledge Center</span>
    </button>
  </div>

  {/* Avatar + Dropdown */}
  <div className="relative flex items-center">
    <Avatar fullName={fullName} size={30} onClick={toggleProfileDropdown} />
    {isProfileDropdownOpen && (
      <div className="absolute top-full right-0 mt-2 bg-white shadow-lg rounded-md w-60 p-4 z-10 text-sm text-gray-800">
        <div className="mb-2">
          <div className="font-medium">{fullName}</div>
          <div className="text-gray-500 text-xs">{user?.email}</div>
        </div>
        <hr className="my-2" />
        <span
          onClick={handleLogout}
          className="block py-2 px-2 text-sm text-red-500 cursor-pointer hover:bg-gray-100 rounded transition-all duration-300"
        >
          Sign Out
        </span>
      </div>
    )}
  </div>

  {/* Hamburger Button */}
  <button
    className="block nav:hidden text-gray-500 focus:outline-none"
    onClick={toggleMenu}
  >
    <svg
      className="w-6 h-6"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
      />
    </svg>
  </button>
</div>

    </header>
  );
};

export default Header;
